import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, ArrowRight, CheckCircle2, Users, BarChart3, Globe, Shield, Zap, TrendingUp } from "lucide-react";
import { SectionHeader } from "@/components/SectionComponents";

const benefits = [
  { icon: Users,     title: "Team-Building Across Borders",  description: "Connect employees through shared language goals, fostering organic relationships across your global offices." },
  { icon: BarChart3, title: "Measurable ROI",               description: "Track language progress and business impact with dashboards built for HR and L&D leaders." },
  { icon: Zap,       title: "Live in 48 Hours",             description: "From contract to first session in under 48 hours. Our onboarding team handles everything." },
  { icon: Shield,    title: "Enterprise-Grade Security",   description: "SOC 2 Type II certified. GDPR compliant. Single Sign-On (SSO). Your data stays yours." },
  { icon: Globe,     title: "Any Scale",                   description: "From 50-person startups to 50,000-employee multinationals. Lingua2 scales seamlessly." },
  { icon: TrendingUp, title: "HR System Integration",     description: "Native integrations with SAP SuccessFactors, Workday, BambooHR, and more via our API." },
];

export default function Enterprise() {
  return (
    <div className="min-h-screen bg-background">

      {/* Hero */}
      <section className="pt-28 pb-20 bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">For Enterprises</p>
              <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground leading-tight mb-5">
                Language Skills That Drive Business Results
              </h1>
              <p className="text-base text-muted-foreground leading-relaxed mb-8">
                Lingua2 helps multinational companies build multilingual workforces through AI-enhanced language exchange — at any scale, in any language.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary font-semibold transition-fast">
                  <Link to="/contact">Request Enterprise Demo <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="transition-fast">
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl-token">
              <img
                src="https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Corporate team"
                className="w-full h-[360px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Why Enterprise Teams Choose Lingua2"
            title="Built for the Modern Multinational"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((b) => (
              <div key={b.title} className="group p-6 rounded-xl border border-border bg-card card-hover hover:border-primary/25">
                <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/14 transition-fast">
                  <b.icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                </div>
                <h3 className="text-sm font-display font-semibold text-foreground mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Talk to Our Enterprise Team <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-surface">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Use Cases" title="Real Business Applications" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: "International Expansion",      desc: "Prepare teams for new markets by training them in local languages before and during expansion." },
              { title: "Cross-Border Collaboration",   desc: "Reduce friction between global offices by building language bridges between teams in different countries." },
              { title: "Expatriate Integration",       desc: "Help international hires settle faster by connecting them with local language partners from day one." },
              { title: "Leadership Development",       desc: "Equip senior leaders with language skills needed for global negotiations and stakeholder management." },
            ].map((uc) => (
              <div key={uc.title} className="p-5 rounded-xl border border-border bg-card flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-display font-semibold text-foreground mb-1">{uc.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-primary">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: "300+", label: "Enterprise Clients" },
              { value: "40%",  label: "Avg. Proficiency Gain" },
              { value: "48h",  label: "Time to First Session" },
              { value: "99.9%", label: "Platform Uptime" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-display font-bold text-primary-foreground mb-1">{s.value}</div>
                <div className="text-sm text-primary-foreground/70">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background text-center">
        <div className="max-w-xl mx-auto px-4">
          <h2 className="text-2xl font-display font-bold text-foreground mb-3">Ready to Scale Language Learning?</h2>
          <p className="text-muted-foreground mb-8 text-sm">Get a personalized enterprise solution for your organization in 48 hours.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Book a Consultation <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button size="lg" asChild variant="outline" className="transition-fast">
              <Link to="/pricing">See Pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
